"use client";
import { z } from "zod";
import Link from "next/link";
import { cn } from "@lib/utils";
import { useState } from "react";
import axios from "@config/axios";
import { format } from "date-fns";
import { AxiosError } from "axios";
import { encrypt } from "@lib/encrypt";
import { useForm } from "react-hook-form";
import { Input } from "@components/ui/input";
import { toast } from "@components/ui/toaster";
import { Button } from "@components/ui/button";
import { organizationTypes, titles } from "@data";
import { Combobox } from "@components/ui/combobox";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { DatePicker } from "@components/ui/date-picker";
import { PhoneInput } from "@components/ui/phone-input";
import usePasswordToggle from "@hooks/usePasswordToggle";
import { useRouter, useSearchParams } from "next/navigation";
import { OrganizationRegisterSchema } from "@schemas/register";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";
import { Building2, CalendarDays, ChevronLeft, ChevronRight, ContactRound, Globe, Handshake, Hash, LockKeyhole, Mail, UserRoundPlus } from "lucide-react";

const RegisterOrganizationForm = () => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const [page, setPage] = useState<1 | 2>(1);
	const urlParams = new URLSearchParams(searchParams?.toString() || "");
	const [onRegisterToast, setOnRegisterToast] = useState<string | number>();
	const { inputIcon: pwdInputIcon, inputType: pwdInputType } = usePasswordToggle();
	const { inputIcon: cPwdInputIcon, inputType: cPwdInputType } = usePasswordToggle();

	const defaultValues: z.infer<typeof OrganizationRegisterSchema> = {
		organization: {
			name: "",
			registrationNo: "",
			dateOfEstablishment: undefined,
			website: "",
			email: "",
			contactNo: "",
			profileType: "",
		},
		user: {
			title: "",
			firstName: "",
			middleName: "",
			lastName: "",
			email: "",
			contactNo: "",
			password: "",
			confirmPassword: "",
		},
	};

	const onTogglePage = async () => {
		const userDetails = await form.trigger("organization");
		if (userDetails) {
			setPage(prevPage => (prevPage === 1 ? 2 : 1));
		} else {
			toast.error("Please fill the required fields!");
		}
	};

	const form = useForm<z.infer<typeof OrganizationRegisterSchema>>({
		defaultValues,
		mode: "onTouched",
		resolver: zodResolver(OrganizationRegisterSchema),
	});

	const onRegister = async (values: z.infer<typeof OrganizationRegisterSchema>) => {
		setOnRegisterToast(toast.loading("Loading...", { description: "Registering account..." }));

		const dateOfEstablishment = values?.organization?.dateOfEstablishment ? format(new Date(values?.organization?.dateOfEstablishment), "yyyy-MM-dd") : "";

		const organization = values.organization;

		const password = encrypt(values?.user?.password);
		const confirmPassword = encrypt(values?.user?.confirmPassword);

		const user = values.user;

		await axios.post("/api/organization/register", {
			organization: { ...organization, dateOfEstablishment },
			user: { ...user, password, confirmPassword },
		});
	};

	const { mutate, isPending } = useMutation({
		mutationFn: onRegister,
		onSuccess: () => {
			toast.success("Success!", { id: onRegisterToast, description: "Registered successfully!" });
			router.replace(`/auth/login${urlParams.toString() ? `?${urlParams.toString()}` : ""}`);
		},
		onError: (error: unknown) => {
			toast.error("Error!", { id: onRegisterToast, description: (error as AxiosError<string>)?.response?.data || "Internal server error!" });
		},
	});

	return (
		<div className="flex w-full max-w-5xl flex-col items-center justify-center gap-6 sm:justify-start">
			<div className="flex w-full flex-col items-center justify-center gap-1">
				<h1 className="text-center text-2xl font-semibold">{`Welcome Aboard`}</h1>
				<p className="text-muted-foreground text-center text-sm">{page === 1 ? "Enter your organization details to continue" : "Enter your personal details to continue"}</p>
			</div>
			<Form {...form}>
				<form className="flex w-full max-w-sm flex-col items-center justify-stretch gap-7 md:max-w-2xl" onSubmit={form.handleSubmit(values => mutate(values))}>
					<div className={cn("w-full", page === 1 ? "grid grid-cols-1 justify-center gap-6 md:grid-cols-2" : "hidden")}>
						<FormField
							control={form.control}
							name="organization.name"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Name<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input placeholder="Enter organization name" startContent={<Building2 size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="organization.registrationNo"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Registration No.<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input placeholder="Enter registration no." startContent={<Hash size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="organization.dateOfEstablishment"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										Date of Establishment<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<DatePicker disabledRange={date => date > new Date() || date < new Date("1900-01-01")} placeholder="Select date of establishment" startContent={<CalendarDays size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="organization.website"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Website<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input placeholder="Enter website" startContent={<Globe size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="organization.email"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Email<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input placeholder="Enter email" startContent={<Mail size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="organization.contactNo"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Contact No.<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<PhoneInput placeholder="Enter contact no." {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="organization.profileType"
							render={({ field }) => (
								<FormItem className="space-y-3 md:col-span-2">
									<FormLabel>
										Register As<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<RadioGroup defaultValue={field.value} onValueChange={field.onChange}>
											{organizationTypes?.map(organizationType => (
												<FormItem className="flex items-center gap-1" key={organizationType?.uuid}>
													<FormControl>
														<RadioGroupItem value={organizationType?.value} />
													</FormControl>
													<FormLabel className="font-normal">{organizationType?.label}</FormLabel>
												</FormItem>
											))}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<div className={cn("w-full", page === 2 ? "grid grid-cols-1 justify-center gap-6 md:grid-cols-2" : "hidden")}>
						<FormField
							control={form.control}
							name="user.title"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Title<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Combobox emptyLabel="No Titles" label="Title" options={titles} placeholder="Select your title" startContent={<Handshake size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="user.firstName"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										First Name<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input placeholder="Enter your first name" startContent={<ContactRound size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="user.middleName"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>Middle Name</FormLabel>
									<FormControl>
										<Input placeholder="Enter your middle name" startContent={<ContactRound size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="user.lastName"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Last Name<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input placeholder="Enter your last name" startContent={<ContactRound size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="user.email"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Email<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input placeholder="Enter your email" startContent={<Mail size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="user.contactNo"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Contact No.<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<PhoneInput placeholder="Enter your contact no." {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="user.password"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Password<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input endContent={pwdInputIcon} placeholder="Enter your password" startContent={<LockKeyhole size={16} />} type={pwdInputType} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="user.confirmPassword"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Confirm Password<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input endContent={cPwdInputIcon} placeholder="Confirm your Password" startContent={<LockKeyhole size={16} />} type={cPwdInputType} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<div className={cn("w-full", page === 1 ? "grid grid-cols-1 items-center justify-center gap-6 md:grid-cols-2" : "hidden")}>
						<Button className="w-full" disabled={isPending} onClick={onTogglePage} size="lg" type="button" variant="default">
							Next
							<ChevronRight size={16} />
						</Button>
						<span className="order-last text-xs md:order-first md:text-sm">
							{`Already have an account? `}
							<Link className="text-secondary text-xs font-medium md:text-sm" href="/auth/login">
								Login Now
							</Link>
						</span>
					</div>
					<div className={cn("w-full", page === 2 ? "grid grid-cols-1 items-center justify-center gap-6 md:grid-cols-2" : "hidden")}>
						<Button className="w-full" disabled={isPending} size="lg" type="submit" variant="default">
							<UserRoundPlus size={16} />
							Register
						</Button>
						<Button className="order-last w-full md:order-first" disabled={isPending} onClick={onTogglePage} size="lg" type="button" variant="ghost">
							<ChevronLeft size={16} />
							Back
						</Button>
					</div>
				</form>
			</Form>
		</div>
	);
};

export default RegisterOrganizationForm;
